import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  BarChart3, MessageSquare, Users, FileText, 
  TrendingUp, Database, ArrowRight, Sparkles
} from 'lucide-react';

const ICONS = {
  conversations: MessageSquare,
  users: Users,
  documents: FileText,
  analytics: BarChart3,
  trending: TrendingUp,
  database: Database,
};

export function EmptyState({ 
  icon = 'analytics', 
  title = 'No data yet',
  description = 'Data will appear here once activity is recorded.',
  action = null,
  actionLabel = 'Get Started',
  actionIcon: ActionIcon = ArrowRight,
  actionVariant = 'outline',
  onAction = null,
  className = '',
  compact = false,
  variant = 'card',
}) {
  const Icon = typeof icon === 'function' ? icon : ICONS[icon] || BarChart3;
  const content = (
    <div className={`flex flex-col items-center justify-center px-6 text-center ${compact ? 'min-h-[128px] py-6' : 'min-h-[180px] py-10'}`}>
      <div className={`${compact ? 'mb-3 h-10 w-10 rounded-2xl' : 'mb-4 h-12 w-12 rounded-2xl'} flex items-center justify-center bg-secondary text-muted-foreground dark:bg-background/40`}>
        <Icon className={`${compact ? 'h-5 w-5' : 'h-6 w-6'} opacity-70`} />
      </div>
      <h3 className={`${compact ? 'text-sm' : 'text-base'} font-semibold text-foreground`}>
        {title}
      </h3>
      {description && (
        <p className="mt-1 max-w-md text-sm text-muted-foreground">
          {description}
        </p>
      )}
      {(action || onAction) && (
        <Button
          variant={actionVariant}
          onClick={onAction}
          className="mt-4"
        >
          {actionLabel}
          {ActionIcon && <ActionIcon className="ml-2 h-4 w-4" />}
        </Button>
      )}
    </div>
  );

  if (variant === 'plain') {
    return (
      <div className={className}>
        {content}
      </div>
    );
  }

  if (variant === 'panel') {
    return (
      <div className={`rounded-xl border border-border bg-card/70 dark:bg-background/20 ${className}`}>
        {content}
      </div>
    );
  }

  return (
    <Card className={`border-dashed border-2 border-border dark:border-border bg-muted/50 dark:bg-card/50 ${className}`}>
      <CardContent className="p-0">
        {content}
      </CardContent>
    </Card>
  );
}

export function EmptyChartPlaceholder({ 
  height = 'h-64',
  title = 'No chart data',
  description = 'Chart will display once data is available.',
}) {
  return (
    <div className={`${height} flex flex-col items-center justify-center bg-secondary dark:bg-card/50 rounded-lg border border-dashed border-border dark:border-border`}>
      <BarChart3 className="w-12 h-12 text-muted-foreground dark:text-muted-foreground mb-3" />
      <p className="text-sm font-medium text-muted-foreground dark:text-muted-foreground">{title}</p>
      <p className="text-xs text-muted-foreground dark:text-muted-foreground mt-1">{description}</p>
    </div>
  );
}

export function EmptyTablePlaceholder({
  columns: _columns = 4,
  rows: _rows = 5,
  title = 'No data to display',
}) {
  return (
    <div className="w-full">
      <div className="flex items-center justify-center py-12 bg-secondary dark:bg-card/50 rounded-lg border border-dashed border-border dark:border-border">
        <div className="text-center">
          <Database className="w-10 h-10 text-muted-foreground dark:text-muted-foreground mx-auto mb-3" />
          <p className="text-sm font-medium text-muted-foreground dark:text-muted-foreground">{title}</p>
          <p className="text-xs text-muted-foreground dark:text-muted-foreground mt-1">Data will appear here once available</p>
        </div>
      </div>
    </div>
  );
}

export function GettingStartedCard({ onAction, onDismiss, onFocusChecklist }) {
  const scrollToChecklist = () => {
    // Highlight the onboarding checklist in the bottom right
    const checklist = document.querySelector('[data-testid="onboarding-checklist"]');
    if (checklist) {
      // Add a highlight effect
      checklist.classList.add('ring-4', 'ring-primary', 'ring-opacity-75');
      checklist.scrollIntoView({ behavior: 'smooth', block: 'end' });
      
      // Remove highlight after animation
      setTimeout(() => {
        checklist.classList.remove('ring-4', 'ring-primary', 'ring-opacity-75');
      }, 2000);
    }
    onFocusChecklist?.();
  };

  return (
    <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/50 dark:to-purple-950/50 border-indigo-200 dark:border-indigo-800 relative overflow-hidden">
      {/* Subtle grid pattern */}
      <div 
        className="absolute inset-0 opacity-10 dark:opacity-5"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(99,102,241,0.4) 1px, transparent 0)`,
          backgroundSize: '20px 20px'
        }}
      />
      
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 transition-colors z-10"
          aria-label="Dismiss welcome banner"
          data-testid="dismiss-welcome-banner"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
      )}
      <CardContent className="py-6 px-6 relative z-10">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-indigo-500/25">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-1">
              Welcome to Eva Insights!
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Your AI-powered HR analytics dashboard is ready. Complete the setup checklist 
              to unlock the full potential of Eva for your organization.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button 
                size="sm" 
                onClick={scrollToChecklist}
                className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white border-0"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                View Setup Checklist
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => onAction?.('kb')}
              >
                <FileText className="w-4 h-4 mr-2" />
                Upload Documents
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default EmptyState;
